import { z } from 'zod'
import type { TokenCreateHook } from '@kernel/config/types.js'

/**
 * Auth module types and schemas
 */

// ============================================================================
// Validation Schemas
// ============================================================================

export const loginSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(1, 'Password required'),
  otp: z.string().length(6).optional(),
  deviceId: z.string().max(64).optional(),
  deviceName: z.string().max(100).optional(),
  captchaToken: z.string().optional()
})

export type LoginInput = z.infer<typeof loginSchema>

export const registerSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  otp: z.string().length(6).optional(),
  deviceId: z.string().max(64).optional(),
  deviceName: z.string().max(100).optional(),
  captchaToken: z.string().optional()
})

export type RegisterInput = z.infer<typeof registerSchema>

export const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email'),
  captchaToken: z.string().optional()
})

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>

export const resetPasswordSchema = z.object({
  email: z.string().email('Invalid email'),
  otp: z.string().length(6, 'OTP must be 6 digits'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters')
})

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>

// ============================================================================
// Interfaces
// ============================================================================

/**
 * Request info for audit and device tracking
 */
export interface RequestInfo {
  ip?: string
  userAgent?: string
  deviceId?: string
  deviceName?: string
}

/**
 * Local type for user data - auth does not import from users module.
 * Defines the fields auth needs for authentication.
 * Note: roles are loaded separately via users service (multi-role support).
 */
export interface AuthUserRecord {
  id: string
  type: 'human' | 'bot' | 'service'
  email: string | null
  password: string | null
  name: string
  created_at: Date
  updated_at: Date
  created_by: string | null
  updated_by: string | null
  [key: string]: unknown
}

/**
 * Refresh token for JWT rotation
 */
export interface RefreshToken {
  id: string
  token: string
  user_id: string
  expires_at: Date
  device_id?: string | null
  device_name?: string | null
  created_at?: Date
}



/**
 * Active user session (for listing devices)
 */
export interface UserSession {
  id: string
  device_id: string | null
  device_name: string | null
  created_at: Date
  expires_at: Date
  is_current: boolean
}

/**
 * External auth identity linking a Nexus user to an OIDC/OAuth provider
 */
export interface AuthIdentity {
  id: string
  user_id: string
  provider: string
  provider_user_id: string
  provider_email: string | null
  metadata: Record<string, unknown> | null
  linked_at: string
  last_login_at: string | null
  created_at?: string
  updated_at?: string
}

/**
 * Input for linking an external identity to a Nexus user
 */
export interface LinkIdentityInput {
  userId: string
  provider: string
  providerUserId: string
  providerEmail?: string | null
  metadata?: Record<string, unknown>
}

// ============================================================================
// CONFIG
// ============================================================================

/**
 * Typed auth configuration (read once at init from SingleService).
 */
export interface AuthConfig {
  secret: string
  accessExpires: string
  refreshExpires: string
  rateLimitMax: number
  rateLimitWindowMs: number
  /** Cookie domain for SSO across subdomains (e.g., '.example.com') */
  cookieDomain?: string
  /** Number of failed attempts before requiring OTP challenge */
  challengeThreshold: number
  /** Skip OTP verification for registration (only for testing) */
  skipRegisterOtp: boolean
  /** Disable self-registration via POST /auth/register */
  disableRegistration: boolean
  /** Disable auto-creation of users on first OIDC login */
  disableAutoCreate: boolean
  /** Hook to add custom claims to JWT (from StartOptions.onTokenCreate) */
  onTokenCreate?: TokenCreateHook
  /** Enable HaveIBeenPwned leaked password check */
  hibpCheck: boolean
  /** Mode: 'block' rejects compromised passwords, 'warn' allows but returns warning */
  hibpMode: 'block' | 'warn'
  /** CAPTCHA provider: 'none' (disabled), 'turnstile', or 'hcaptcha' */
  captchaProvider: 'none' | 'turnstile' | 'hcaptcha'
  /** CAPTCHA site key (public, used by frontend widget) */
  captchaSiteKey: string
  /** CAPTCHA secret key (server-side verification) */
  captchaSecretKey: string
  /** Require CAPTCHA on login endpoint */
  captchaOnLogin: boolean
  /** Require CAPTCHA on register endpoint */
  captchaOnRegister: boolean
  /** Require CAPTCHA on forgot-password endpoint */
  captchaOnForgotPassword: boolean
}

/**
 * Build AuthConfig from SingleService row + resolved secret.
 * Pure function — no env reads, no side effects.
 */
export function buildAuthConfig(row: Record<string, unknown> | null, secret: string): AuthConfig {
  return {
    secret,
    accessExpires: (row?.['access_expires'] as string) ?? '15m',
    refreshExpires: (row?.['refresh_expires'] as string) ?? '7d',
    rateLimitMax: (row?.['rate_limit_max'] as number) ?? 5,
    rateLimitWindowMs: ((row?.['rate_limit_window'] as number) ?? 900) * 1000,
    cookieDomain: (row?.['cookie_domain'] as string) || undefined,
    challengeThreshold: (row?.['challenge_threshold'] as number) ?? 2,
    skipRegisterOtp: row?.['skip_register_otp'] === true || row?.['skip_register_otp'] === 1,
    disableRegistration: row?.['disable_registration'] === true || row?.['disable_registration'] === 1,
    disableAutoCreate: row?.['disable_auto_create'] === true || row?.['disable_auto_create'] === 1,
    hibpCheck: row?.['hibp_check'] === true || row?.['hibp_check'] === 1,
    hibpMode: (row?.['hibp_mode'] as 'block' | 'warn') ?? 'block',
    captchaProvider: (row?.['captcha_provider'] as 'none' | 'turnstile' | 'hcaptcha') ?? 'none',
    captchaSiteKey: (row?.['captcha_site_key'] as string) ?? '',
    captchaSecretKey: (row?.['captcha_secret_key'] as string) ?? '',
    captchaOnLogin: row?.['captcha_on_login'] === true || row?.['captcha_on_login'] === 1,
    captchaOnRegister: row?.['captcha_on_register'] === true || row?.['captcha_on_register'] === 1,
    captchaOnForgotPassword: row?.['captcha_on_forgot_password'] === true || row?.['captcha_on_forgot_password'] === 1
  }
}

// ============================================================================
// Personal Access Tokens
// ============================================================================

export const createPersonalTokenSchema = z.object({
  name: z.string().min(1).max(100),
  scope: z.enum(['readonly', 'readwrite']),
  expires_at: z.string().datetime().optional()
})

export type CreatePersonalTokenInput = z.infer<typeof createPersonalTokenSchema>

export type PersonalTokenScope = 'readonly' | 'readwrite'

export interface PersonalToken {
  id: string
  user_id: string
  name: string
  token_prefix: string
  token_hash: string
  scope: PersonalTokenScope
  expires_at: Date | null
  last_used_at: Date | null
  created_at: Date
}
